import moment from 'moment';
import * as QueryString from 'query-string';
import { convertDate, getHumanizeTime } from './dateUtils';
import { osndLocalStorage } from './localStorageUtils';
import { roles, userAllowed } from './utils';

const _ = require('lodash');

export const TICKETING_CHART_TYPE = {
    CRITICAL_AVERAGE_RESTORATION_TIME: 'CRITICAL_AVERAGE_RESTORATION_TIME',
    HIGH_AVERAGE_RESTORATION_TIME: 'HIGH_AVERAGE_RESTORATION_TIME',
    AVERAGE_INITIAL_NOTIFICATION: 'AVERAGE_INITIAL_NOTIFICATION',
    AVERAGE_NOTIFICATION: 'AVERAGE_NOTIFICATION'
};

export const REPORT_NAVIGATION = {
    RESTORATION_TIME: '/maintenance/ticketing/monitoring/reporting',
    RESPONSE_NOTIFICATION_TIME: '/maintenance/ticketing/monitoring/reporting/response-notification-time',
    AVAILABILITY: '/maintenance/ticketing/monitoring/reporting/availability',
    MAIN: '/maintenance/ticketing'
};

export const RESTORATION_TYPE = {
    CRITICAL: 'Critical',
    HIGH: 'High'
};

export const REPORT_TYPE = {
    RESTORATION_TIME: 'Restoration time',
    AVAILABILITY: 'Availability',
    DOWN_TIME: 'DownTime',
    TICKET_DETAILS: 'Tickets details'
};

export const CATEGORY_CONSTANTS = {
    CAT_1: {
        heading: 'Critical Severity Restorations',
        sectionNote: {
            chart: 'Figure 1-1: Restorations (Severity: Critical)',
            table: 'Table 1-1: Restoration Ticket Details (Severity: Critical)'
        }
    },
    CAT_2: {
        heading: 'High Severity Restorations',
        sectionNote: {
            chart: 'Figure 2-1: Restorations (Severity: High)',
            table: 'Table 2-1: Restoration Ticket Details (Severity: High)'
        }
    },
    CAT_3: {
        heading: 'Availability',
        sectionNote: {
            chart: 'Figure 1-1: Availability based on ticketing data',
            table: 'Table 1-1: Responses Ticket Details'
        }
    },
    CAT_4: {
        heading: 'Ticket Details',
        sectionNote: {
            chart: 'Figure 1-1: Tickets',
            table: 'Table 1-1: Ticket Details'
        }
    }
};

const NOTIFICATION_KEY_MAPPING = {
    ticketReferenceNumber: 'Ticket number',
    problemDescription: 'Description',
    initialResponseDate: 'Initial response',
    lastUpdateDate: 'Last update',
    status: 'Status',
    subproblemType: 'Problem sub type',
    openedBy: 'Opened by',
    createdDate: 'Created'
};

const RESTORATION_KEY_MAPPING = {
    ticketReferenceNumber: 'Ticket number',
    problemDescription: 'Description',
    createdDate: 'Created date',
    severity: 'Severity',
    restorationTime: REPORT_TYPE.RESTORATION_TIME,
    subproblemType: 'Problem sub type',
    lastUpdateDate: 'Last updated',
    openedBy: 'Opened by',
    status: 'Status',
    completedDate: 'Completed date'
};

const TICKETING_KEY_MAPPING = {
    ticketReferenceNumber: 'Ticket number',
    problemDescription: 'Problem description',
    createdDate: 'Created date',
    severity: 'Severity',
    restorationTime: REPORT_TYPE.RESTORATION_TIME,
    subproblemType: 'Problem type',
    lastUpdateDate: 'Last updated',
    openedBy: 'Opened by',
    status: 'Status',
    completedDate: 'Completed date',
    enterpriseId: 'Location',
    networkTicketHostName: 'Network element name',
    commonName: 'Common name'
};

export const buildTicketingPieChartData = (type, data) => {
    let pirChartData = {};
    if (data) {
        if (type === TICKETING_CHART_TYPE.CRITICAL_AVERAGE_RESTORATION_TIME || type === TICKETING_CHART_TYPE.HIGH_AVERAGE_RESTORATION_TIME) {
            pirChartData = buildTicketingRestorationPieData(data);
        } else {
            pirChartData = buildTicketingResponseNotificationPieData(data);
        }
    }

    return pirChartData;
};

const averageTimeFormat = (pending, averageInitialDuration) => {
    if (averageInitialDuration !== '-') {
        return pending > 0 ? `${averageInitialDuration}+` : averageInitialDuration;
    }
    return '---';
};

export const buildTicketingRestorationPieData = ({ chart = {}, totalTime = 0 }) => {
    const { group_a = 0, group_b = 0, group_c = 0, pending = 0 } = chart;
    const grp1 = { name: `0-4 hours`, value: group_a };
    const grp2 = { name: '5-8 hours', value: group_b };
    const grp3 = { name: '> 8 hours', value: group_c };
    const grp4 = { name: 'Pending', value: pending };
    const totalTickets = group_a + group_b + group_c + pending;
    const averageInitialDuration = getHumanizeTime(moment.duration(totalTime / totalTickets));

    return {
        chartData: [grp1, grp2, grp3, grp4],
        total: totalTickets,
        averageTime: averageTimeFormat(pending, averageInitialDuration),
        legendWidth: 160,
        pieX: 185,
        centerContentX: 190
    };
};

export const ticketChartMap = {
    '0-4 hours': 'group_a',
    '5-8 hours': 'group_b',
    '> 8 Hours': 'group_c',
    Pending: 'pending',
    '< 15 mins': 'group_a',
    '> 15 mins': 'group_b'
};

export const buildTicketingResponseNotificationPieData = ({ chart = {}, totalTime = 0 }) => {
    const { group_a = 0, group_b = 0, pending = 0 } = chart;
    let grp1 = { name: `< 15 mins`, value: group_a };
    let grp2 = { name: '> 15 mins', value: group_b };
    let grp3 = { name: 'Pending', value: pending };
    const totalTickets = group_a + group_b + pending;
    const averageInitialDuration = getHumanizeTime(moment.duration(totalTime / totalTickets));
    return {
        chartData: [grp1, grp2, grp3],
        total: totalTickets,
        averageTime: averageTimeFormat(pending, averageInitialDuration)
    };
};

export const parseSort = (querySort) => {
    let sort = querySort;

    switch (sort) {
        case 'number':
            sort = 'ticketReferenceNumber';
            break;
        case 'type':
            sort = 'subproblemType';
            break;
        case 'updated':
            sort = 'lastUpdateDate';
            break;
        case 'description':
            sort = 'problemDescription';
            break;
        case 'status':
            break;
        case 'none':
            sort = null;
            break;
        default:
            sort = 'ticketReferenceNumber';
            break;
    }

    return sort;
};

export const buildSort = (sort) => {
    let querySort = sort;

    switch (sort) {
        case 'ticketReferenceNumber':
            querySort = 'ticketReferenceNumber';
            break;
        case 'subproblemType':
            querySort = 'subproblemType';
            break;
        case 'lastUpdateDate':
            querySort = 'updated';
            break;
        case 'createdDate':
            querySort = 'createdDate';
            break;
        case 'completedDate':
            querySort = 'completedDate';
            break;
        case 'problemDescription':
            querySort = 'problemDescription';
            break;
        case 'restorationTime':
            querySort = 'restorationTime';
            break;
        case 'status':
            break;
        case 'none':
            querySort = null;
            break;
        default:
            querySort = 'ticketReferenceNumber';
            break;
    }

    return querySort;
};

export const buildPageUrl = (auth, page, { pageNum = 1, count, status, textSearch, search, sort, desc, fromDate, toDate }) => {
    let path = page;

    const paramsObj = {
        status // blank is removed below
    };

    if (pageNum > 1) {
        // only if greater then 1
        path += `/${pageNum}`;
    }

    if (count !== 10 || count < 100) {
        // only if not 10 and less than 100
        paramsObj.count = count;
    }

    if (sort === null) {
        // blank is unsorted
        paramsObj.sort = 'none';
    } else if (sort !== 'ticketReferenceNumber') {
        // blank or name is removed
        paramsObj.sort = buildSort(sort);
    }

    if (desc === true) {
        // only add if true
        paramsObj.desc = '1';
    }

    if (search) {
        Object.keys(search).forEach((searchKey) => {
            paramsObj[searchKey] = search[searchKey] || null;
        });
    }

    if (!!fromDate) {
        paramsObj.fromDate = fromDate;
    }

    if (!!toDate) {
        paramsObj.toDate = toDate;
    }

    if (textSearch !== null) {
        paramsObj.textSearch = textSearch;
    }

    const params = QueryString.stringify(paramsObj, {
        skipNull: true,
        skipEmptyString: true
    });

    if (params !== '') {
        path += `?${params}`;
    }

    const { enterpriseId } = auth;

    if (enterpriseId) {
        path = `${path}#${enterpriseId}`;
    }

    return path;
};

export const getExportNotificationTableDetails = (content, type, solutionType) => {
    let body = [];
    let head = [['SI', 'Ticket number', 'Description', 'Initial response', 'Last updated', 'Status']];
    const sourceFilter = (item) => {
        return type === TICKETING_CHART_TYPE.AVERAGE_INITIAL_NOTIFICATION
            ? item?.source === 'EPBTICKET'
            : solutionType === 'NDAC'
            ? item?.source === 'NOKIATICKET'
            : item?.source === 'NTWKTICKET';
    };

    let userPreference = osndLocalStorage.getObject('userPreference', {});
    if (userPreference?.responseNotificationPreference) {
        const preferenceCols = userPreference?.responseNotificationPreference.filter((col) => col.visible && col.id !== 'id');
        head = [['SI', ...preferenceCols.map(({ id }) => NOTIFICATION_KEY_MAPPING[id])]];
        content.filter(sourceFilter).forEach((item, index) => {
            let itemData = preferenceCols.map(({ id }) => {
                if (id === 'initialResponseDate' || id === 'createdDate' || id === 'lastUpdateDate') {
                    return item[id] ? convertDate(item[id], false) : '';
                }
                if (id === 'subproblemType') {
                    if (item[id] === 'NETWORKDASHBOARD') {
                        return 'NETWORK DASHBOARD';
                    } else if (item[id] === 'SOFTWAREUPGRADE') {
                        return 'SOFTWARE UPGRADE';
                    }
                }
                return item[id];
            });
            itemData = [index + 1, ...itemData];
            body.push({ ...itemData });
        });
    } else {
        content.filter(sourceFilter).forEach(({ ticketReferenceNumber, problemDescription, status, initialResponseDate, lastUpdateDate }, index) => {
            body.push([
                index + 1,
                ticketReferenceNumber,
                problemDescription || '',
                initialResponseDate ? convertDate(initialResponseDate, false) : '',
                lastUpdateDate ? convertDate(lastUpdateDate, false) : '',
                status
            ]);
        });
    }

    return {
        head,
        body
    };
};

export const getExportRestorationTableDetails = (content, severity) => {
    let body = [];
    let head = [['SI', 'Ticket', 'Description', 'Created date', 'Severity', REPORT_TYPE.RESTORATION_TIME]];
    const sourceFilter = (item) => {
        return !!item?.severity
            ? severity === RESTORATION_TYPE.CRITICAL
                ? item?.severity.toLowerCase() === 'critical'
                : item?.severity.toLowerCase() === 'high'
            : false;
    };
    let userPreference = osndLocalStorage.getObject('userPreference', {});
    if (userPreference?.initialResponsePreference) {
        const preferenceCols = userPreference?.initialResponsePreference.filter(
            (col) => col.visible && col.id !== 'id' && col.id !== 'problemTypeDescription'
        );
        head = [['SI', ...preferenceCols.map(({ id }) => RESTORATION_KEY_MAPPING[id])]];
        content.filter(sourceFilter).forEach((item, index) => {
            let itemData = preferenceCols.map(({ id }) => {
                if (id === 'createdDate' || id === 'completedDate' || id === 'lastUpdateDate') {
                    return item[id] ? convertDate(item[id], false) : '';
                }
                if (id === 'restorationTime') {
                    return getHumanizeTime(item[id]);
                }
                if (id === 'subproblemType') {
                    if (item[id] === 'NETWORKDASHBOARD') {
                        return 'NETWORK DASHBOARD';
                    } else if (item[id] === 'SOFTWAREUPGRADE') {
                        return 'SOFTWARE UPGRADE';
                    }
                }
                return item[id];
            });
            itemData = [index + 1, ...itemData];
            body.push({ ...itemData });
        });
    } else {
        content.filter(sourceFilter).forEach(({ ticketReferenceNumber, problemDescription, createdDate, severity, restorationTime }, index) => {
            body.push([
                index + 1,
                ticketReferenceNumber,
                problemDescription || '',
                createdDate ? convertDate(createdDate, false) : '',
                severity,
                getHumanizeTime(restorationTime)
            ]);
        });
    }

    return {
        head,
        body
    };
};

export const getExportAvailabilityTableDetails = (content, severity) => {
    let body = [];
    let head = [['SI', 'Ticket', 'Description', 'Severity', 'Restoration time', 'Created date']];
    const sourceFilter = (item) => {
        return !!item?.severity
            ? severity === RESTORATION_TYPE.CRITICAL
                ? item?.severity.toLowerCase() === 'critical'
                : item?.severity.toLowerCase() === 'high'
            : false;
    };
    let userPreference = osndLocalStorage.getObject('userPreference', {});
    if (userPreference?.availabilityTickets) {
        const preferenceCols = userPreference?.availabilityTickets.filter((col) => col.visible && col.id !== 'id');
        head = [['SI', ...preferenceCols.map(({ id }) => RESTORATION_KEY_MAPPING[id])]];
        content.filter(sourceFilter).forEach((item, index) => {
            let itemData = preferenceCols.map(({ id }) => {
                if (id === 'createdDate' || id === 'completedDate') {
                    return item[id] ? convertDate(item[id], false) : '';
                }
                if (id === 'restorationTime') {
                    return getHumanizeTime(item[id]);
                }
                if (id === 'subproblemType') {
                    if (item[id] === 'NETWORKDASHBOARD') {
                        return 'NETWORK DASHBOARD';
                    } else if (item[id] === 'SOFTWAREUPGRADE') {
                        return 'SOFTWARE UPGRADE';
                    }
                }
                return item[id];
            });
            itemData = [index + 1, ...itemData];
            body.push({ ...itemData });
        });
    } else {
        content.filter(sourceFilter).forEach(({ ticketReferenceNumber, problemDescription, createdDate, severity, restorationTime }, index) => {
            body.push([
                index + 1,
                ticketReferenceNumber,
                problemDescription || '',
                severity || '',
                getHumanizeTime(restorationTime),
                createdDate ? convertDate(createdDate, false) : ''
            ]);
        });
    }

    return {
        head,
        body
    };
};

const mapSubProblemType = (subProblemType) => {
    if (subProblemType === 'NETWORKDASHBOARD') {
        return 'NETWORK DASHBOARD';
    } else if (subProblemType === 'SOFTWAREUPGRADE') {
        return 'SOFTWARE UPGRADE';
    }

    return subProblemType;
};

const mapTicketingTableCellValue = (enterpriseMap, item, columnId) => {
    if (columnId === 'createdDate' || columnId === 'completedDate' || columnId === 'lastUpdateDate') {
        return item[columnId] ? convertDate(item[columnId], false) : '';
    }

    if (columnId === 'restorationTime') {
        return getHumanizeTime(item[columnId]);
    }

    if (columnId === 'enterpriseId') {
        return enterpriseMap[item[columnId]] || '';
    }

    if (columnId === 'subproblemType') {
        return mapSubProblemType(item[columnId]);
    }

    return item[columnId] || '';
};

const getColumnHeaderName = (columnId) => {
    if (TICKETING_KEY_MAPPING[columnId]) {
        return TICKETING_KEY_MAPPING[columnId];
    }
    return columnId.replace(/([A-Z])/g, ' $1').trim();
};

const getTicketingTableBody = (content, columnIds, enterpriseMap) => {
    return content.map((item, index) => {
        let itemData = columnIds.map((columnId) => mapTicketingTableCellValue(enterpriseMap, item, columnId));
        return [index + 1, ...itemData];
    });
};

export const getAllColumnCsvTicketingTableDetails = (content, enterpriseMap, isAllLocations) => {
    const userPreference = osndLocalStorage.getObject('userPreference', {});
    const csvRows = [];

    if (userPreference.ticketingPreference) {
        let preferenceColumns = userPreference.ticketingPreference.filter((column) => column.id !== 'id').map((column) => column.id);

        if (isAllLocations && !preferenceColumns.includes('enterpriseId')) {
            preferenceColumns.push('enterpriseId');
        }

        if (!isAllLocations) {
            preferenceColumns = preferenceColumns.filter((columnId) => columnId !== 'enterpriseId');
        }

        csvRows.push(['SI', ...preferenceColumns.map((columnId) => TICKETING_KEY_MAPPING[columnId])]);
        csvRows.push(...getTicketingTableBody(content, preferenceColumns, enterpriseMap));
    } else if (isAllLocations) {
        csvRows.push(['SI', 'Ticket number', 'Problem description', 'Problem type', 'Status', 'Severity', 'Location', 'Last updated']);
        const columnIds = ['ticketReferenceNumber', 'problemDescription', 'subproblemType', 'status', 'severity', 'enterpriseId', 'lastUpdateDate'];
        csvRows.push(...getTicketingTableBody(content, columnIds, enterpriseMap));
    } else {
        csvRows.push(['SI', 'Ticket number', 'Problem description', 'Problem type', 'Status', 'Severity', 'Last updated']);
        const columnIds = ['ticketReferenceNumber', 'problemDescription', 'subproblemType', 'status', 'severity', 'lastUpdateDate'];
        csvRows.push(...getTicketingTableBody(content, columnIds, enterpriseMap));
    }

    return csvRows;
};

export const getExportTicketingTableDetails = (content, enterpriseMap, is_all_locations) => {
    let body = [];
    let head = [['SI', 'Ticket number', 'Problem description', 'Problem type', 'Status', 'Last updated']];
    let userPreference = osndLocalStorage.getObject('userPreference', {});
    if (userPreference?.ticketingPreference && userPreference.ticketingPreference.length > 0) {
        let preferenceCols = userPreference?.ticketingPreference.filter((col) => col.visible && col.id !== 'id').map((col) => col.id);
        if (preferenceCols.length === 0) {
            preferenceCols = userPreference?.ticketingPreference.filter((col) => col.id !== 'id').map((col) => col.id);
        }
        if (!is_all_locations) {
            preferenceCols = preferenceCols.filter((columnId) => columnId !== 'enterpriseId');
        }
        head = [['SI', ...preferenceCols.map(getColumnHeaderName)]];
        body = getTicketingTableBody(content, preferenceCols, enterpriseMap);
    } else {
        const columnIds = ['ticketReferenceNumber', 'problemDescription', 'subproblemType', 'status', 'lastUpdateDate'];
        body = getTicketingTableBody(content, columnIds, enterpriseMap);
    }

    return {
        head,
        body
    };
};

export const getPathFromRoles = (reportRoles = []) => {
    if (userAllowed(reportRoles, [roles.MANAGE_SERVICE_LEVEL_RESORATION_TIME])) {
        return REPORT_NAVIGATION.RESTORATION_TIME;
    } else if (userAllowed(reportRoles, [roles.MANAGE_SERVICE_LEVEL_RESPONSE_NOTIF_TIME])) {
        return REPORT_NAVIGATION.RESPONSE_NOTIFICATION_TIME;
    } else if (userAllowed(reportRoles, [roles.MANAGE_SERVICE_LEVEL_AVAILABILITY, roles.MANAGE_SERVICE_LEVEL_AVAILABILITY_PLUS], true)) {
        return REPORT_NAVIGATION.AVAILABILITY;
    }
};

export const restorationSeverityOptions = [
    { key: 'Critical', text: 'Critical', value: 'Critical' },
    { key: 'High', text: 'High', value: 'High' }
];

export const resNotificationSeverityOptions = (isNDAC) => [
    { key: 'EPBTICKET', text: 'Initial response', value: 'EPBTICKET' },
    { key: isNDAC ? 'NOKIATICKET' : 'NTWKTICKET', text: 'Notification', value: isNDAC ? 'NOKIATICKET' : 'NTWKTICKET' }
];

export const restorationCategoryOptions = [
    { key: 'group_a', text: '0-4 hours', value: '0-4 hours' },
    { key: 'group_b', text: '5-8 hours', value: '5-8 hours' },
    { key: 'group_d', text: 'Pending', value: 'Pending' },
    { key: 'group_c', text: '>8 hours', value: '>8 hours' }
];

export const resNotoficationCategoryOptions = [
    { key: 'group_a', text: '< 15 mins', value: '< 15 mins' },
    { key: 'group_b', text: '> 15 mins', value: '> 15 mins' },
    { key: 'group_c', text: 'Pending', value: 'Pending' }
];

export const pieCategoryMapping = {
    '0-4hours': 'group_a',
    '5-8hours': 'group_b',
    '>8hours': 'group_c',
    Pending: 'group_d',
    '<15mins': 'group_a',
    '>15mins': 'group_b'
};

export const STATUS_OPTIONS = [
    { key: 'Open', text: 'Open', value: 'Open' },
    { key: 'Closed', text: 'Closed', value: 'Closed' }
];

export const getPayloadFilter = (search, isAPIRequest = false) => {
    const updatedFilter = {};

    let { filterType, appliedFilter, ...restSearch } = search;
    Object.keys(restSearch).forEach((key) => {
        if (search[key] !== '' && key !== 'appliedFilter' && key !== 'filterType') {
            if (isAPIRequest) {
                if (key === 'searchCategory') {
                    const category = search[key];
                    updatedFilter[key] = pieCategoryMapping[category.split(' ').join('')];
                } else {
                    updatedFilter[key] = search[key];
                }
            } else {
                updatedFilter[key] = search[key];
            }
        }
    });
    return updatedFilter;
};
